/**
 * StockfishEngine React component (Workstream B, consumer-facing entry).
 *
 * Drives the SF18 typed UCI layer (engine.ts) + standard-UCI parser (parser.ts)
 * and re-emits results through the SAME `onResult` callback contract the old
 * custom-WASM component exposed, so every consumer is unchanged:
 *
 *   onResult(variationNumber, movesSAN, baseTurn, score, isMate, raw, searchedFen)
 *
 * (`searchedFen` is a later, optional addition — see OnResult below.)
 *
 * What changed vs the old component:
 *   - Real `uci`/`uciok` + `isready`/`readyok` handshake before searching.
 *   - Standard UCI parsing; SAN derived client-side via chess.js; baseTurn
 *     derived from the FEN (no custom `baseTurn`/`pvSan`/`bmc` fields).
 *   - Search-token race handling: on FEN/settings change we stop -> await ack
 *     -> go, debounce rapid changes, and drop stale lines from prior searches.
 *   - Worker crash -> automatic reinit; unsupported browser -> graceful no-op.
 *
 * NOTE: `movesSAN` is now a CLEAN SAN array (no `pvSan`/`bmc` sentinel tokens).
 * Consumers that previously stripped those sentinels have been updated.
 */

import {useEffect, useRef} from 'react';
import {
  StockfishEngineClient,
  isEngineSupported,
  EngineInfoLine,
} from './engine';
import {parseInfoLine, baseTurnFromFen} from './parser';

/**
 * F6 (fix F-B, 2026-10-06): the engine's own lifecycle, for a consumer that
 * wants to show something other than silence while it is not ready —
 * `PlayChessV2`'s `rail/analysis/EngineHost` is the first (and so far only)
 * one. OPTIONAL and called from the EXISTING lifecycle only: no caller that
 * omits it sees any change in behaviour.
 */
export type EngineStatus = 'unsupported' | 'loading' | 'ready' | 'error';

export type OnResult = (
  variationNumber: number,
  movesSAN: string[],
  baseTurn: 'w' | 'b',
  score: string,
  isMate: boolean,
  raw: string,
  /**
   * The FEN this line was actually searched for. Appended as a trailing arg
   * so every existing consumer is unchanged, but consumers that act on a
   * result (rather than just display it) should use THIS rather than whatever
   * position their own state currently holds — the two differ in the window
   * between a move landing and the search restarting.
   */
  searchedFen?: string,
) => void;

interface StockfishEngineProps {
  fen: string;
  depth?: number;
  variations?: number;
  /**
   * Worker LIFECYCLE. Turning this off terminates the underlying worker;
   * turning it back on pays for a fresh spawn, WASM instantiation and the full
   * uci/isready handshake. Keep it stable for as long as a consumer might want
   * the engine again soon — to pause searching without that cost, use
   * `searchEnabled`.
   */
  engineOn?: boolean;
  /**
   * Whether the (already running) engine should be SEARCHING. Defaults to
   * true, so every existing consumer is unchanged.
   *
   * Exists because the two questions are genuinely separate and conflating
   * them is expensive: the practice opponent only wants a search on its own
   * turn, but driving that through `engineOn` tore the worker down and rebuilt
   * it on every single ply, which delayed each reply by a full engine boot.
   * With this, the worker stays warm and only the search stops.
   */
  searchEnabled?: boolean;
  /** Hard time cap per search, ms. Defaults to the 10s analysis watchdog. */
  movetime?: number;
  /** Limit playing strength to ~this Elo. Omit for full strength. */
  elo?: number | null;
  onResult: OnResult;
  /**
   * The move the engine actually CHOSE, from the UCI `bestmove` line.
   *
   * Not the same thing as the first move of the top `info ... pv` line, and
   * the difference is the whole point: Stockfish applies its UCI_Elo handicap
   * when it picks `bestmove` (Skill::pick_best swaps in a deliberately
   * sub-optimal root move at the end of the search), while the `info` PVs keep
   * reporting the objectively best line it found. Anything that PLAYS a move
   * at a limited strength must therefore read it from here — reading the PV
   * instead silently plays at full strength whatever Elo was requested.
   *
   * `searchedFen` is the position this move was actually computed for; act on
   * it rather than on whatever position the consumer's own state holds.
   */
  onBestMove?: (uci: string, searchedFen: string) => void;
  /**
   * F6: the worker's lifecycle, straight from this component's own init /
   * handshake / crash handling — no polling, no guessing from `onResult`
   * never firing. `detail` is the underlying error message, only set for
   * 'error'. Optional; omitting it changes nothing else here.
   */
  onStatus?: (status: EngineStatus, detail?: string) => void;
}

const DEBOUNCE_MS = 150;
const DEFAULT_MOVETIME_MS = 10000;

const StockfishEngine = ({
  fen,
  depth,
  variations,
  engineOn,
  searchEnabled = true,
  movetime,
  elo,
  onResult,
  onBestMove,
  onStatus,
}: StockfishEngineProps) => {
  const clientRef = useRef<StockfishEngineClient | null>(null);
  const readyRef = useRef(false);
  // FEN that the engine's CURRENT search is analyzing — used so a parsed line's
  // SAN/baseTurn are computed against the right position.
  const activeFenRef = useRef<string>(fen);
  const onResultRef = useRef(onResult);
  const onBestMoveRef = useRef(onBestMove);
  const onStatusRef = useRef(onStatus);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guard against reinit storms after a worker crash.
  const reinitInFlightRef = useRef(false);
  // Mirror the latest props into refs so startSearch (invoked from the
  // mount-once init effect and the crash-reinit path) never reads stale values.
  const propsRef = useRef({
    fen,
    depth,
    variations,
    engineOn,
    searchEnabled,
    movetime,
    elo,
  });

  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  useEffect(() => {
    onBestMoveRef.current = onBestMove;
  }, [onBestMove]);

  useEffect(() => {
    onStatusRef.current = onStatus;
  }, [onStatus]);

  useEffect(() => {
    propsRef.current = {
      fen,
      depth,
      variations,
      engineOn,
      searchEnabled,
      movetime,
      elo,
    };
  }, [fen, depth, variations, engineOn, searchEnabled, movetime, elo]);

  // ---- engine lifecycle (init / reinit on crash / destroy) ----
  useEffect(() => {
    if (!isEngineSupported()) {
      // Unsupported browser: graceful no-op (B6) — but no longer a SILENT
      // one (F6): the caller asked, so it is told, regardless of `engineOn`.
      onStatusRef.current?.('unsupported');
      return;
    }
    if (!engineOn) {
      return;
    }

    let cancelled = false;

    const handleInfo = (line: EngineInfoLine) => {
      const client = clientRef.current;
      if (!client) {
        return;
      }
      // Drop stale lines from a previous search/position (B4).
      if (line.token !== client.currentToken) {
        return;
      }
      const parsed = parseInfoLine(line.raw, activeFenRef.current);
      if (!parsed) {
        return;
      }
      onResultRef.current(
        parsed.variationNumber,
        parsed.movesSAN,
        parsed.baseTurn,
        parsed.score,
        parsed.isMate,
        line.raw,
        activeFenRef.current,
      );
    };

    const handleBestMove = (bestMove: string, token: number) => {
      const client = clientRef.current;
      if (!client) {
        return;
      }
      // Same staleness rule the info lines get. `(none)` is Stockfish's answer
      // when the position has no legal moves — not a move, so don't emit it.
      if (token !== client.currentToken) {
        return;
      }
      if (!bestMove || bestMove === '(none)') {
        return;
      }
      onBestMoveRef.current?.(bestMove, activeFenRef.current);
    };

    const handleError = (err: Error) => {
      // Worker crash -> reinit once (B6).
      if (
        cancelled ||
        reinitInFlightRef.current ||
        !propsRef.current.engineOn
      ) {
        return;
      }
      // F6: a post-init crash IS an error, whether or not the reinit below
      // recovers — a silent flip straight back to 'loading' would hide the
      // crash that just happened.
      onStatusRef.current?.('error', err?.message);
      reinitInFlightRef.current = true;
      readyRef.current = false;
      try {
        clientRef.current?.destroy();
      } catch {
        /* noop */
      }
      clientRef.current = null;
      void initEngine();
    };

    const initEngine = async () => {
      const client = new StockfishEngineClient({
        onInfo: handleInfo,
        onBestMove: handleBestMove,
        onError: handleError,
      });
      clientRef.current = client;
      try {
        await client.init();
        if (cancelled) {
          client.destroy();
          return;
        }
        readyRef.current = true;
        reinitInFlightRef.current = false;
        onStatusRef.current?.('ready');
        // If we should already be analyzing, kick it off post-(re)init using
        // the LATEST props (refs), not the stale values this effect closed over.
        if (propsRef.current.engineOn && propsRef.current.searchEnabled) {
          startSearch();
        }
      } catch (e) {
        // Init failed (load failure / timeout / unsupported): still a no-op
        // for the caller (B6), but no longer a SILENT one. An engine that
        // never starts is otherwise indistinguishable from one that started
        // and simply never found a move — which is exactly what made the
        // practice opponent's failure so hard to place.
        readyRef.current = false;
        reinitInFlightRef.current = false;
        const message = e instanceof Error ? e.message : String(e);
        console.warn('[engine] Stockfish failed to initialise:', message);
        onStatusRef.current?.('error', message);
      }
    };

    onStatusRef.current?.('loading');
    void initEngine();

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      clientRef.current?.destroy();
      clientRef.current = null;
      readyRef.current = false;
    };
  }, [engineOn]);

  // Issue a fresh search for the LATEST props (read from refs so this is safe
  // to call from the mount-once init/reinit path too). Stops any prior search
  // first (token bump discards its stale lines), then sets position + go.
  const startSearch = () => {
    const client = clientRef.current;
    if (!client || !readyRef.current) {
      return;
    }
    const {
      fen: curFen,
      depth: curDepth,
      variations: curVariations,
      movetime: curMovetime,
      elo: curElo,
    } = propsRef.current;
    client.stop(); // bumps token; discards stale lines from the previous FEN
    client.newGame();
    activeFenRef.current = curFen;
    client.analyze(curFen, {
      depth: curDepth,
      multiPV: curVariations,
      movetime: curMovetime ?? DEFAULT_MOVETIME_MS,
      ...(typeof curElo === 'number' ? {elo: curElo} : {}),
    });
  };

  // ---- react to FEN / depth / variations / engineOn changes (B4 debounce) ----
  useEffect(() => {
    if (!isEngineSupported()) {
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (engineOn && searchEnabled) {
      debounceRef.current = setTimeout(() => {
        startSearch();
      }, DEBOUNCE_MS);
    } else {
      // Engine off, or paused via `searchEnabled`: stop the current search
      // (token bump drops stragglers). The worker itself is left alone — only
      // the `engineOn` lifecycle effect above ever destroys it.
      clientRef.current?.stop();
    }

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fen, depth, variations, engineOn, searchEnabled, movetime, elo]);

  return null;
};

export default StockfishEngine;

// Re-export for the rare consumer (and the shared engine page) that needs the
// pure helpers without the React wrapper.
export {parseInfoLine, baseTurnFromFen};
